import api, { noAuthApi } from './api';
import { unwrap } from './http';
import type { AuthUser, StaffRole } from '@/redux/slices/authSlice';
import {
  mockExchangeStoreToken,
  mockIdentify,
  mockStoresForUser,
  type IdentifyResponse,
  type MockStore,
} from '@/mocks/auth';
import { withMockFallback } from '@/utils/queryMock';
import { USE_MOCK } from '@/utils/env';

// ============================================================
// REQUEST TYPES (snake_case cho API JSON per ADR-013 D2)
// ============================================================

export interface LoginCredentials {
  /** SĐT (0xxxxxxxxx) hoặc email — BE auto-detect (per cd0a8f8). */
  identifier: string;
  password: string;
  /** Tùy chọn — chỉ cần khi 1 identifier login nhiều tenant. */
  tenant_id?: string;
  /** reCAPTCHA token — BE yêu cầu khi đăng nhập sai nhiều lần. */
  recaptcha_token?: string;
}

/** Legacy 1-step register — POST /auth/register (RegisterDto, snake_case). */
export interface RegisterPayload {
  store_name: string;
  store_address?: string;
  city_id?: string;
  ward_id?: string;
  owner_name?: string;
  owner_email?: string;
  owner_phone: string;
  owner_password?: string;
  plan_slug?: 'lite' | 'starter' | 'professional' | 'enterprise';
}

export type RegisterOtpChannel = 'email' | 'zns' | 'sms';

/** POST /auth/register/init — snake_case per ADR-013 D2. */
export interface RegisterInitPayload {
  store_name: string;
  store_address?: string;
  city_id: string;
  ward_id?: string;
  owner_name?: string;
  owner_email?: string;
  owner_phone: string;
  plan_slug?: string;
  preferred_channel?: RegisterOtpChannel;
}

/** EG-356 — BE response shape snake_case (ADR-013 §D2 pass-through, không
 *  transform). Consumer (Register page) tự map sang state navigate camelCase
 *  khi cần (state là TS variable scope — D5 cho phép camelCase). */
export interface RegisterInitResponse {
  session_id: string;
  email_masked?: string; // chỉ có khi user truyền ownerEmail
  phone_masked: string;
  channel: RegisterOtpChannel;
  available_channels: RegisterOtpChannel[];
  expires_in: number;
}

/** BE RegisterVerifyOtpDto dùng camelCase: sessionId, otp. */
export interface RegisterVerifyOtpPayload {
  sessionId: string;
  otp: string;
}

/**
 * Recovery OTP — flow cho account đã đăng ký nhưng `must_set_password=true`.
 * BE RecoverInitDto dùng camelCase: identifier, preferredChannel.
 */
export interface RecoverInitPayload {
  identifier: string;
  preferredChannel?: RegisterOtpChannel;
}

/**
 * Response từ POST /auth/recover/init — BE thực tế trả snake_case:
 * session_id, email_masked, phone_masked, available_channels, expires_in.
 */
export interface RecoverInitResponse {
  session_id: string;
  email_masked?: string;
  phone_masked?: string;
  channel: RegisterOtpChannel;
  available_channels: RegisterOtpChannel[];
  expires_in: number;
}

/** BE RecoverVerifyOtpDto dùng camelCase: sessionId, otp. */
export interface RecoverVerifyOtpPayload {
  sessionId: string;
  otp: string;
}

/** BE RecoverResendOtpDto dùng camelCase: sessionId, channel. */
export interface RecoverResendOtpPayload {
  sessionId: string;
  channel?: RegisterOtpChannel;
}

/** BE SetPasswordDto dùng camelCase: newPassword, confirmPassword. */
export interface SetPasswordPayload {
  newPassword: string;
  confirmPassword: string;
}

// ============================================================
// RESPONSE TYPES — đã chuẩn hoá về camelCase trong service
// ============================================================

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
  must_set_password?: boolean;
  must_change_password?: boolean;
  is_phone_verified?: boolean;
  needs_store_selection?: boolean;
  requires_mfa_setup?: boolean;
}

/** BE ChangePasswordDto dùng camelCase: oldPassword, newPassword. */
export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export interface RegisterResponse extends LoginResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan_name: string;
    status: string;
    trial_ends_at: string | null;
  };
  store: { id: string; code: string; name: string };
}

export interface RefreshResponse {
  access_token: string;
  refresh_token?: string;
}

// ============================================================
// HELPERS — BE wrap `{ data: { ... }, success: true }`. Unwrap về flat.
// `unwrap` extracted sang src/services/http.ts để order/product/inventory reuse.
// ============================================================

export interface BeUser {
  _id?: string;
  customer_id?: string;
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  tenant_id?: string;
  role?: string;
  role_ids?: string[];
  /** Permission keys — BE resolves server-side từ role/user. */
  permissions?: string[];
  store_id?: string;
  store_assignments?: Array<{ store_id?: string; is_primary?: boolean }>;
  delivery_store_id?: string;
}

export function toAuthUser(u: BeUser): AuthUser {
  const id = u.customer_id || u._id || u.id || '';
  return {
    id,
    name: u.name || '',
    email: u.email || '',
    phone: u.phone || '',
    role: ((u.role as StaffRole) || 'owner') as StaffRole,
    roleIds:
      u.role_ids && u.role_ids.length > 0
        ? u.role_ids
        : u.role === 'owner'
          ? ['role-sys-owner']
          : [],
    permissions: u.permissions ?? [],
    tenantId: u.tenant_id || '',
    storeId: u.store_id,
    storeAssignments: (u.store_assignments ?? [])
      .map((a) => String(a.store_id ?? ''))
      .filter(Boolean),
    deliveryStoreId: u.delivery_store_id || undefined,
  };
}

export function normalizeVietnamPhone(value?: string): string {
  const phone = (value ?? '').trim().replace(/[\s().-]+/g, '');

  if (phone.startsWith('+84')) {
    return `0${phone.slice(3)}`;
  }

  if (phone.startsWith('84')) {
    return `0${phone.slice(2)}`;
  }

  return phone;
}

// ============================================================
// SERVICE
// ============================================================

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Login must use public client to avoid auth-refresh interceptor redirect loops.
    const { data: raw } = await noAuthApi.post('/auth/login', {
      identifier: credentials.identifier,
      password: credentials.password,
      ...(credentials.tenant_id ? { tenant_id: credentials.tenant_id } : {}),
    });
    const payload = unwrap<{
      user?: BeUser;
      tokens?: { access_token?: string; refresh_token?: string };
      must_set_password?: boolean;
      must_change_password?: boolean;
      is_phone_verified?: boolean;
      needs_store_selection?: boolean;
      requires_mfa_setup?: boolean;
    }>(raw);
    return {
      access_token: payload.tokens?.access_token ?? '',
      refresh_token: payload.tokens?.refresh_token ?? '',
      user: toAuthUser(payload.user || {}),
      must_set_password: !!payload.must_set_password,
      must_change_password: !!payload.must_change_password,
      is_phone_verified: !!payload.is_phone_verified,
      needs_store_selection: !!payload.needs_store_selection,
      requires_mfa_setup: !!payload.requires_mfa_setup,
    };
  },

  /**
   * Step 1 của login 2-bước (EG-366) — kiểm tra email tồn tại + list tenants.
   */
  identify: async (identifier: string): Promise<IdentifyResponse> => {
    if (USE_MOCK) return mockIdentify(identifier);
    const { data: raw } = await noAuthApi.post('/auth/identify', { identifier });
    const payload = unwrap<{
      user_exists?: boolean;
      has_password?: boolean;
      tenants?: Array<Record<string, unknown>>;
    }>(raw);
    return {
      userExists: !!payload.user_exists,
      hasPassword: !!payload.has_password,
      tenants: (payload.tenants ?? []).map((t) => ({
        tenantId: String(t.tenant_id ?? ''),
        tenantCode: String(t.tenant_code ?? ''),
        name: String(t.name ?? ''),
        role: (t.role ?? 'owner') as IdentifyResponse['tenants'][number]['role'],
        planTier: t.plan_name as IdentifyResponse['tenants'][number]['planTier'],
        storeCount: Number(t.store_count ?? 0),
      })),
    };
  },

  /**
   * Đổi token sau khi cashier chọn store (EG-367).
   */
  exchangeStoreToken: async (storeId: string): Promise<{ access_token: string; refresh_token: string }> => {
    return withMockFallback(
      async () => {
        const { data: raw } = await api.post('/auth/exchange-store-token', { store_id: storeId });
        const payload = unwrap<{ access_token?: string; refresh_token?: string }>(raw);
        return {
          access_token: payload.access_token ?? '',
          refresh_token: payload.refresh_token ?? '',
        };
      },
      async () => mockExchangeStoreToken(storeId),
      { label: 'auth.exchangeStoreToken' },
    );
  },

  /**
   * Lấy danh sách store user có quyền truy cập.
   */
  listMyStores: async (): Promise<MockStore[]> => {
    return withMockFallback<MockStore[]>(
      async () => {
        const { data: raw } = await api.get('/auth/my-stores');
        const payload = unwrap<Array<Record<string, unknown>>>(raw);
        const list = Array.isArray(payload) ? payload : [];
        return list.map((s) => ({
          storeId: String(s.store_id ?? s._id ?? ''),
          storeCode: String(s.store_code ?? s.code ?? ''),
          name: String(s.name ?? ''),
          address: s.address ? String(s.address) : undefined,
        }));
      },
      async () => mockStoresForUser,
      { label: 'auth.myStores' },
    );
  },

  changePassword: async (body: ChangePasswordPayload) => {
    // BE ChangePasswordDto expect camelCase: oldPassword, newPassword.
    const { data: raw } = await api.post('/auth/change-password', {
      oldPassword: body.oldPassword,
      newPassword: body.newPassword,
    });
    return unwrap<{ success?: boolean; message?: string }>(raw);
  },

  logout: async (accessToken?: string) => {
    const { data: raw } = await api.post('/auth/logout', accessToken ? { access_token: accessToken } : {});
    return unwrap(raw);
  },

  refresh: async (refreshToken: string): Promise<RefreshResponse> => {
    // BE chấp nhận cả refresh_token (snake) và refreshToken (camel).
    const { data: raw } = await api.post('/auth/refresh', { refresh_token: refreshToken });
    const payload = unwrap<{ access_token?: string; refresh_token?: string }>(raw);
    return {
      access_token: payload.access_token ?? '',
      refresh_token: payload.refresh_token,
    };
  },

  /**
   * Bước 1 register 2-step — gửi OTP.
   * BE RegisterInitDto dùng camelCase → gửi body trực tiếp.
   * Response BE thực tế dùng snake_case: session_id, email_masked, phone_masked,
   * available_channels, expires_in.
   */
  registerInit: async (
    body: RegisterInitPayload,
  ): Promise<RegisterInitResponse> => {
    const { data: raw } = await api.post('/auth/register/init', {
      ...body,
      owner_phone: normalizeVietnamPhone(body.owner_phone),
    });
    return unwrap<RegisterInitResponse>(raw);
  },

  /**
   * Recovery OTP — bước 1: gửi OTP cho account `must_set_password=true`.
   * BE RecoverInitDto dùng camelCase → gửi body trực tiếp.
   * Response BE thực tế dùng snake_case: session_id, email_masked, phone_masked,
   * available_channels, expires_in.
   */
  recoverInit: async (
    body: RecoverInitPayload,
  ): Promise<RecoverInitResponse> => {
    const { data: raw } = await noAuthApi.post('/auth/recover/init', body);
    return unwrap<RecoverInitResponse>(raw);
  },

  /**
   * Recovery OTP — bước 2: verify OTP. BE RecoverVerifyOtpDto dùng camelCase.
   */
  recoverVerifyOtp: async (
    body: RecoverVerifyOtpPayload,
  ): Promise<LoginResponse> => {
    const { data: raw } = await noAuthApi.post('/auth/recover/verify-otp', body);
    const payload = unwrap<{
      user?: BeUser;
      tokens?: { access_token?: string; refresh_token?: string };
      must_set_password?: boolean;
      is_phone_verified?: boolean;
    }>(raw);
    return {
      access_token: payload.tokens?.access_token ?? '',
      refresh_token: payload.tokens?.refresh_token ?? '',
      user: toAuthUser(payload.user || {}),
      must_set_password: !!payload.must_set_password,
      is_phone_verified: !!payload.is_phone_verified,
    };
  },

  /** Recovery OTP — gửi lại OTP. BE trả `{ channel, expires_in }`. */
  recoverResendOtp: async (body: RecoverResendOtpPayload) => {
    const { data: raw } = await noAuthApi.post('/auth/recover/resend-otp', body);
    return unwrap<{ channel: RegisterOtpChannel; expires_in: number }>(raw);
  },

  /**
   * Bước 2 register 2-step — verify OTP, BE atomic create tenant + return tokens.
   * Response BE: { tenant: { tenant_id, name, slug, plan_name, status, trial_ends_at },
   *               store: { store_id, code, name }, owner, tokens, must_set_password }
   */
  registerVerifyOtp: async (
    body: RegisterVerifyOtpPayload,
  ): Promise<RegisterResponse> => {
    const { data: raw } = await api.post('/auth/register/verify-otp', body);
    const payload = unwrap<{
      tenant?: {
        tenant_id?: string;
        id?: string;
        name?: string;
        slug?: string;
        plan_name?: string;
        status?: string;
        trial_ends_at?: string | null;
      };
      store?: { store_id?: string; id?: string; code?: string; name?: string };
      owner?: BeUser;
      tokens?: { access_token?: string; refresh_token?: string };
      must_set_password?: boolean;
      is_phone_verified?: boolean;
    }>(raw);
    const tenantId = payload.tenant?.tenant_id ?? payload.tenant?.id ?? '';
    const storeId = payload.store?.store_id ?? payload.store?.id ?? '';
    return {
      access_token: payload.tokens?.access_token ?? '',
      refresh_token: payload.tokens?.refresh_token ?? '',
      user: toAuthUser({
        ...(payload.owner ?? {}),
        tenant_id: tenantId,
        role: 'owner',
        store_id: storeId,
      }),
      tenant: {
        id: tenantId,
        name: payload.tenant?.name ?? '',
        slug: payload.tenant?.slug ?? '',
        plan_name: payload.tenant?.plan_name ?? '',
        status: payload.tenant?.status ?? '',
        trial_ends_at: payload.tenant?.trial_ends_at ?? null,
      },
      store: {
        id: storeId,
        code: payload.store?.code ?? '',
        name: payload.store?.name ?? '',
      },
      must_set_password: !!payload.must_set_password,
      is_phone_verified: !!payload.is_phone_verified,
    };
  },

  /**
   * EG-356 Sprint 6 — Đặt password lần đầu sau OTP verify.
   * BE SetPasswordDto dùng camelCase: newPassword, confirmPassword.
   */
  setPassword: async (
    body: SetPasswordPayload,
  ): Promise<LoginResponse> => {
    const { data: raw } = await api.post('/auth/set-password', {
      newPassword: body.newPassword,
      confirmPassword: body.confirmPassword,
    });
    const payload = unwrap<{
      user?: BeUser;
      tokens?: { access_token?: string; refresh_token?: string };
      must_set_password?: boolean;
    }>(raw);
    return {
      access_token: payload.tokens?.access_token ?? '',
      refresh_token: payload.tokens?.refresh_token ?? '',
      user: toAuthUser(payload.user || {}),
      must_set_password: !!payload.must_set_password,
    };
  },

  /** Gửi lại OTP đăng ký. BE RegisterResendOtpDto dùng camelCase. BE trả `{ channel, expires_in }`. */
  registerResendOtp: async (
    sessionId: string,
    channel?: RegisterOtpChannel,
  ): Promise<{ channel: RegisterOtpChannel; expires_in: number }> => {
    const { data: raw } = await api.post('/auth/register/resend-otp', {
      sessionId,
      ...(channel ? { channel } : {}),
    });
    return unwrap<{ channel: RegisterOtpChannel; expires_in: number }>(raw);
  },

  /**
   * Legacy 1-step register — POST /auth/register (RegisterDto, snake_case).
   * Đồng bộ với BE RegisterDto: store_name, store_address, owner_name, owner_email,
   * owner_phone, owner_password, plan_slug. KHÔNG có city_id/ward_id trong RegisterDto.
   */
  register: async (body: RegisterPayload): Promise<RegisterResponse> => {
    const { data: raw } = await api.post('/auth/register', {
      store_name: body.store_name,
      store_address: body.store_address,
      owner_name: body.owner_name,
      owner_email: body.owner_email,
      owner_phone: normalizeVietnamPhone(body.owner_phone),
      owner_password: body.owner_password,
      plan_slug: body.plan_slug,
    });
    const payload = unwrap<{
      tenant?: {
        tenant_id?: string;
        id?: string;
        name?: string;
        slug?: string;
        plan_name?: string;
        status?: string;
        trial_ends_at?: string | null;
      };
      store?: { store_id?: string; id?: string; code?: string; name?: string };
      owner?: BeUser;
      tokens?: { access_token?: string; refresh_token?: string };
    }>(raw);
    const tenantId = payload.tenant?.tenant_id ?? payload.tenant?.id ?? '';
    const storeId = payload.store?.store_id ?? payload.store?.id ?? '';
    return {
      access_token: payload.tokens?.access_token ?? '',
      refresh_token: payload.tokens?.refresh_token ?? '',
      user: toAuthUser({
        ...(payload.owner ?? {}),
        tenant_id: tenantId,
        role: 'owner',
        store_id: storeId,
      }),
      tenant: {
        id: tenantId,
        name: payload.tenant?.name ?? '',
        slug: payload.tenant?.slug ?? '',
        plan_name: payload.tenant?.plan_name ?? '',
        status: payload.tenant?.status ?? '',
        trial_ends_at: payload.tenant?.trial_ends_at ?? null,
      },
      store: {
        id: storeId,
        code: payload.store?.code ?? '',
        name: payload.store?.name ?? '',
      },
    };
  },

  /**
   * Quên mật khẩu (tenant web, email link). Constant-time response — luôn 200.
   */
  forgotPasswordEmail: async (email: string) => {
    const { data: raw } = await noAuthApi.post('/auth/forgot-password-email', { email });
    return unwrap<{ message: string }>(raw);
  },

  /**
   * Đặt lại mật khẩu bằng token từ email link.
   * BE ResetPasswordDto dùng camelCase: token, newPassword.
   */
  resetPasswordWithToken: async (token: string, newPassword: string) => {
    const { data: raw } = await noAuthApi.post('/auth/reset-password', {
      token,
      newPassword,
    });
    return unwrap<{ success: boolean; message: string }>(raw);
  },

  /** Legacy phone-OTP flow (giữ cho backward / mobile app). */
  forgotPassword: async (phone: string, tenantId: string) => {
    const { data: raw } = await noAuthApi.post('/auth/forgot-password', {
      phone,
      tenant_id: tenantId,
    });
    return unwrap(raw);
  },

  /** Verify OTP của flow quên mật khẩu theo SĐT (public). */
  verifyForgotPasswordOtp: async (
    phone: string,
    otp: string,
    tenantId: string,
  ) => {
    const { data: raw } = await noAuthApi.post('/auth/forgot-password/verify', {
      phone,
      otp,
      tenant_id: tenantId,
    });
    return unwrap(raw);
  },

  resetPassword: async (body: { phone: string; newPassword: string; tenantId: string }) => {
    const { data: raw } = await noAuthApi.post('/auth/forgot-password/reset', {
      phone: body.phone,
      new_password: body.newPassword,
      tenant_id: body.tenantId,
    });
    return unwrap(raw);
  },

  me: async (): Promise<AuthUser> => {
    const { data: raw } = await api.get('/auth/me');
    // BE `/auth/me` trả shape `{ user, tenant, stores, default_store_id, permissions }`
    // — KHÁC `/auth/login` (login dùng `user.toJSON()` flat). Phải bóc `payload.user`
    // thay vì truyền cả payload vào toAuthUser. Fallback derive `store_assignments`
    // từ top-level `stores` cho client chạy với BE legacy chưa project field này.
    const payload = unwrap<{
      user?: BeUser;
      stores?: Array<{ id?: string }>;
      default_store_id?: string | null;
      permissions?: string[];
    }>(raw);
    const userPayload: BeUser = payload.user ?? {};
    const storeAssignments =
      userPayload.store_assignments && userPayload.store_assignments.length > 0
        ? userPayload.store_assignments
        : (payload.stores ?? [])
            .map((s) => ({ store_id: s.id }))
            .filter((a): a is { store_id: string } => !!a.store_id);
    return toAuthUser({
      ...userPayload,
      permissions: userPayload.permissions ?? payload.permissions,
      store_assignments: storeAssignments,
      delivery_store_id:
        userPayload.delivery_store_id ?? payload.default_store_id ?? undefined,
    });
  },
};
